import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WelcomeModal.module.scss';

type Props = {
  onDismiss: () => void;
};

const WelcomeModal: FC<Props> = ({ onDismiss }) => {
  const [t] = useTranslation('dashboard');
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onDismiss(); }}
    >
      <div className={styles.card}>
        <button className={styles.closeBtn} onClick={onDismiss} aria-label={t('deadlinesMessages.welcome.close', 'Stäng')}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        <div className={styles.visual}>
          <DeadlineIllustration />
        </div>

        <div className={styles.body}>
          <span className={styles.chip}>{t('deadlinesMessages.welcome.chip', 'Nyhet')}</span>
          <h2 className={styles.title}>{t('deadlinesMessages.welcome.title', 'Att göra')}</h2>
          <p className={styles.lead}>
            {t('deadlinesMessages.welcome.lead', 'Vi har samlat dina deadlines och aktiviteter på ett ställe, så att du slipper gå in i varje projekt separat för att hålla koll.')}
          </p>
          <ul className={styles.list}>
            <li>{t('deadlinesMessages.welcome.list.anbudsinlamning', 'Anbud som behöver utvärderas')}</li>
            <li>{t('deadlinesMessages.welcome.list.aktiviteter', 'Aktiviteter med deadlines')}</li>
            <li>{t('deadlinesMessages.welcome.list.inkopKlart', 'Inköp som ska vara klara')}</li>
            <li>{t('deadlinesMessages.welcome.list.forfraganSkickasSenast', 'Förfrågningar som ska skickas senast')}</li>
            <li>{t('deadlinesMessages.welcome.list.ramavtal', 'Ramavtal som är på väg att löpa ut')}</li>
          </ul>
          <p className={styles.coming}>
            <strong>{t('deadlinesMessages.welcome.comingSoonLabel', 'Kommer snart:')}</strong>{' '}
            {t('deadlinesMessages.welcome.comingSoon', 'En samlad inkorg för meddelanden från leverantörer, direkt utan att behöva gå in i varje projekt.')}
          </p>
          <button className={styles.cta} onClick={onDismiss}>
            {t('deadlinesMessages.welcome.cta', 'Kom igång')}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeadlineIllustration: FC = () => (
  <svg viewBox="0 0 440 160" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* Row 1 — overdue, red */}
    <rect x="12" y="10" width="416" height="42" rx="8" fill="white" opacity="0.85"/>
    <rect x="12" y="10" width="4" height="42" rx="2" fill="#EF4444"/>
    <rect x="26" y="19" width="130" height="8" rx="4" fill="#374151"/>
    <rect x="26" y="33" width="90" height="6" rx="3" fill="#9CA3AF"/>
    <rect x="310" y="18" width="76" height="20" rx="10" fill="#FEE2E2"/>
    <rect x="319" y="25" width="58" height="6" rx="3" fill="#EF4444"/>
    <circle cx="412" cy="31" r="10" fill="none" stroke="#D1D5DB" strokeWidth="1.5"/>
    <path d="M407 31l3 3 5-5" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>

    {/* Row 2 — soon, yellow */}
    <rect x="12" y="59" width="416" height="42" rx="8" fill="white" opacity="0.85"/>
    <rect x="12" y="59" width="4" height="42" rx="2" fill="#F59E0B"/>
    <rect x="26" y="68" width="105" height="8" rx="4" fill="#374151"/>
    <rect x="26" y="82" width="75" height="6" rx="3" fill="#9CA3AF"/>
    <rect x="310" y="67" width="76" height="20" rx="10" fill="#FEF3C7"/>
    <rect x="319" y="74" width="58" height="6" rx="3" fill="#F59E0B"/>
    <circle cx="412" cy="80" r="10" fill="none" stroke="#D1D5DB" strokeWidth="1.5"/>
    <path d="M407 80l3 3 5-5" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>

    {/* Row 3 — ok, gray, checked */}
    <rect x="12" y="108" width="416" height="42" rx="8" fill="white" opacity="0.6"/>
    <rect x="12" y="108" width="4" height="42" rx="2" fill="#D1D5DB"/>
    <rect x="26" y="117" width="118" height="8" rx="4" fill="#9CA3AF"/>
    <rect x="26" y="131" width="82" height="6" rx="3" fill="#D1D5DB"/>
    <rect x="310" y="116" width="76" height="20" rx="10" fill="#F3F4F6"/>
    <rect x="319" y="123" width="58" height="6" rx="3" fill="#9CA3AF"/>
    <circle cx="412" cy="129" r="10" fill="#10B981" stroke="none"/>
    <path d="M407 129l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default WelcomeModal;
